/**
 * "Galerie de la formation" — section inserted into FormationDetail.
 * Pulls only photos tagged to this formation across all its sessions.
 * Renders nothing when there are no tagged photos so we never show an
 * empty placeholder slab.
 */

import { useState } from 'react';
import { useGalleryByFormation } from '../data/queries';
import { trackEvent } from '../lib/track';
import { GalleryLightbox } from './GalleryLightbox';

type Props = {
  formationId: string;
  formationTitle: string;
};

const MAX_VISIBLE = 12;

export function FormationGallery({ formationId, formationTitle }: Props) {
  const galleryQ = useGalleryByFormation(formationId);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);

  const items = galleryQ.data ?? [];
  if (galleryQ.isLoading) return null;
  if (items.length === 0) return null;

  const visible = showAll ? items : items.slice(0, MAX_VISIBLE);

  return (
    <section
      style={{
        padding: '64px 0 24px',
        borderTop: '0.5px solid var(--mtc-line, rgba(15,25,22,0.1))',
        marginTop: 48,
      }}
      aria-label={`Galerie de la formation ${formationTitle}`}
    >
      <header style={{ marginBottom: 24 }}>
        <span className="mtc-eyebrow">Galerie</span>
        <h2
          style={{
            fontFamily: 'var(--mtc-font-display)',
            fontSize: 'clamp(26px, 3vw, 36px)',
            fontWeight: 400,
            lineHeight: 1.05,
            letterSpacing: '-0.02em',
            margin: '8px 0 0',
          }}
        >
          Galerie de la formation
        </h2>
        <p
          style={{
            marginTop: 8,
            fontSize: 14,
            color: 'rgba(15,25,22,0.6)',
          }}
        >
          {items.length} photo{items.length > 1 ? 's' : ''} des sessions précédentes
        </p>
      </header>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 10,
        }}
      >
        {visible.map((item, i) => (
          <button
            type="button"
            key={item.id}
            onClick={() => {
              trackEvent('gallery_open');
              setActiveIndex(i);
            }}
            style={{
              all: 'unset',
              cursor: 'zoom-in',
              display: 'block',
              aspectRatio: '4/3',
              borderRadius: 6,
              overflow: 'hidden',
              background: 'rgba(15,25,22,0.05)',
            }}
            aria-label={item.caption || (item.kind === 'video' ? `Vidéo ${i + 1}` : `Photo ${i + 1}`)}
          >
            {item.kind === 'video' ? (
              <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                <video
                  src={`${item.url}#t=0.1`}
                  preload="metadata"
                  muted
                  playsInline
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'grid',
                    placeItems: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 999,
                      background: 'rgba(0,0,0,0.55)',
                      color: 'white',
                      fontSize: 16,
                      display: 'grid',
                      placeItems: 'center',
                    }}
                  >
                    ▶
                  </div>
                </div>
              </div>
            ) : (
              <img
                src={item.url}
                alt={item.caption ?? ''}
                loading="lazy"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  transition: 'transform 0.3s cubic-bezier(0.2,0.8,0.2,1)',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLImageElement).style.transform = 'scale(1.04)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLImageElement).style.transform = '';
                }}
              />
            )}
          </button>
        ))}
      </div>

      {items.length > MAX_VISIBLE && !showAll && (
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <button
            type="button"
            className="mtc-btn mtc-btn-ghost"
            onClick={() => setShowAll(true)}
          >
            Voir les {items.length - MAX_VISIBLE} photos restantes
          </button>
        </div>
      )}

      <GalleryLightbox items={items} activeIndex={activeIndex} onChange={setActiveIndex} />
    </section>
  );
}
